// Returns a nearby shade of the given color to use as a subtle container/background

function mixWith(
  color: { r: number; g: number; b: number },
  target: { r: number; g: number; b: number },
  amount: number,
) {
  const a = clamp(amount, 0, 1);
  return {
    r: color.r * (1 - a) + target.r * a,
    g: color.g * (1 - a) + target.g * a,
    b: color.b * (1 - a) + target.b * a,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function toRgb(
  color: string,
): { r: number; g: number; b: number; a: number } | null {
  if (!color) return null;
  const rgba = color.match(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?\)/i,
  );
  if (rgba) {
    const r = clamp(parseInt(rgba[1], 10), 0, 255);
    const g = clamp(parseInt(rgba[2], 10), 0, 255);
    const b = clamp(parseInt(rgba[3], 10), 0, 255);
    const a = rgba[4] ? clamp(parseFloat(rgba[4]), 0, 1) : 1;
    return { r, g, b, a };
  }

  const hexMatch = color.match(/^#([0-9a-f]{3,8})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3)
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    if (hex.length === 4)
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    if (hex.length === 6) hex = hex + "ff";
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const a = parseInt(hex.slice(6, 8), 16) / 255;
    return { r, g, b, a };
  }

  return null;
}

function toHex({ r, g, b }: { r: number; g: number; b: number }): string {
  const rr = clamp(Math.round(r), 0, 255).toString(16).padStart(2, "0");
  const gg = clamp(Math.round(g), 0, 255).toString(16).padStart(2, "0");
  const bb = clamp(Math.round(b), 0, 255).toString(16).padStart(2, "0");
  return `#${rr}${gg}${bb}`;
}

export default function deriveShade(
  baseColor: string,
  delta: number = 0.153,
): string {
  const rgb = toRgb(baseColor);
  if (!rgb) return "#f9f9f9";

  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;

  const max = Math.max(rgb.r, rgb.g, rgb.b);
  const min = Math.min(rgb.r, rgb.g, rgb.b);
  const saturation = max > 0 ? (max - min) / max : 0;

  let target: { r: number; g: number; b: number };
  let adjustedDelta = delta;

  if (saturation > 0.7) {
    if (luminance > 0.3) {
      const gray = Math.round(luminance * 255 * 0.4);
      target = { r: gray, g: gray, b: gray };
      adjustedDelta = 0.4;
    } else {
      target = { r: 255, g: 255, b: 255 };
      adjustedDelta = delta * 2;
    }
  } else {
    target =
      luminance > 0.5 ? { r: 0, g: 0, b: 0 } : { r: 255, g: 255, b: 255 };
  }

  const mixed = mixWith(rgb, target, adjustedDelta);
  return toHex(mixed);
}
